import {
  array,
  discriminatedUnion,
  enum as enum_,
  literal,
  object,
  optional,
  string,
  type ZodError,
} from 'zod';

import { QuizSchema } from './quiz';

/**
 * 퀴즈 아이템의 출처 메타데이터.
 * - 'ai': AI 생성. generationId가 동반되어 ClickHouse 분석 시 server biz_generation_assignment와 join됨
 * - 'manual': 수동 작성
 * - 'duplicate': quiz_set 복제로 발생
 * - 'similar': AI 유사 문제 생성
 */
const QuizItemOriginSchema = object({
  type: enum_(['ai', 'manual', 'duplicate', 'similar']),
  generationId: optional(string()),
});

export const QuizItemFormSchema = QuizSchema.Base.and(
  object({
    subjectiveData: QuizSchema.Subjective,
    multipleData: QuizSchema.Multiple,
    oxData: QuizSchema.OX,
    essayData: QuizSchema.Essay,
    matchData: QuizSchema.Match,
    // ⚠️ 다른 유형 데이터와 달리 optional 이다. `getBlankQuizItem()` 이 항상 채워 주므로
    // 실제로는 비지 않지만, 필수로 바꾸면 orderData 를 모르는 기존 픽스처
    // (스토리 1 · 테스트 4)가 전부 깨진다. 제출 검증(:79)에서는 required 라
    // order 유형이 여기까지 오면 값이 있는 것이 보장된다.
    orderData: optional(QuizSchema.Order),
    origin: optional(QuizItemOriginSchema),
  }),
);

export const AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE =
  'At least one quiz is required';

export const BuildQuizListFormSchema = object({
  quizList: array(QuizItemFormSchema).min(
    1,
    AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE,
  ),
});

export const getBuildQuizListSchema = () => {
  return BuildQuizListFormSchema;
};

export type BuildQuizListItem = Zod.infer<typeof QuizItemFormSchema>;

export const ValidateQuizListSchema = array(
  QuizSchema.Base.and(
    discriminatedUnion('type', [
      object({
        type: literal('subjective'),
        subjectiveData: QuizSchema.Subjective,
      }),
      object({
        type: literal('multiple'),
        multipleData: QuizSchema.Multiple,
      }),
      object({
        type: literal('ox'),
        oxData: QuizSchema.OX,
      }),
      object({
        type: literal('essay'),
        essayData: QuizSchema.Essay,
      }),
      object({
        type: literal('match'),
        matchData: QuizSchema.Match,
      }),
      object({
        type: literal('order'),
        orderData: QuizSchema.Order,
      }),
    ]),
  ),
).min(1, AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE);

export const validateQuizList = (
  quizList: BuildQuizListItem[],
): {
  success: boolean;
  error: ZodError | undefined;
} => {
  let success = true;
  let error: ZodError | undefined = undefined;

  const { success: validateSuccess, error: validateError } =
    ValidateQuizListSchema.safeParse(quizList);

  if (!validateSuccess) {
    success = false;
    error = validateError;
  }

  return { success, error };
};

export const getFirstQuizValidationErrorPath = (
  error?: ZodError,
): string | undefined => {
  const firstError = error?.errors[0];
  if (!firstError) return;

  const [quizIndex, dataType, column] = firstError.path;
  if (
    dataType !== 'matchData' ||
    (column !== 'leftItems' && column !== 'rightItems')
  ) {
    return firstError.path.join('.');
  }

  const matchItemErrors = error.errors.filter(
    ({ path }) =>
      path[0] === quizIndex &&
      path[1] === 'matchData' &&
      (path[2] === 'leftItems' || path[2] === 'rightItems') &&
      typeof path[3] === 'number',
  );
  const targetError = matchItemErrors.reduce((first, current) => {
    const getOrder = ({ path }: (typeof matchItemErrors)[number]) =>
      Number(path[3]) * 2 + (path[2] === 'rightItems' ? 1 : 0);

    return getOrder(current) < getOrder(first) ? current : first;
  }, firstError);
  const targetPath =
    targetError.path.length === 4
      ? [...targetError.path, 'text']
      : targetError.path;

  return targetPath.join('.');
};

export const validateQuizItem = (
  quizItem: BuildQuizListItem,
): {
  success: boolean;
  error: ZodError | undefined;
} => {
  const itemSchema = QuizSchema.Base.and(
    discriminatedUnion('type', [
      object({
        type: literal('subjective'),
        subjectiveData: QuizSchema.Subjective,
      }),
      object({
        type: literal('multiple'),
        multipleData: QuizSchema.Multiple,
      }),
      object({
        type: literal('ox'),
        oxData: QuizSchema.OX,
      }),
      object({
        type: literal('essay'),
        essayData: QuizSchema.Essay,
      }),
      object({
        type: literal('match'),
        matchData: QuizSchema.Match,
      }),
      object({
        type: literal('order'),
        orderData: QuizSchema.Order,
      }),
    ]),
  );

  const result = itemSchema.safeParse(quizItem);

  return {
    success: result.success,
    error: result.success ? undefined : result.error,
  };
};
